import React, { useEffect, useState } from "react";
import { CardContainer } from "../../components/Card/CardContainer";
import {
  Col,
  Row,
  DatePicker,
  Tabs,
  Tag,
  Table,
  Switch,
  Modal,
  Divider,
  ConfigProvider,
} from "antd";
import dayjs from "dayjs";
import Button from "../../components/Button/Button";
import { useNavigate } from "react-router";
import Input from "../../components/Input/Input";
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  DeleteOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { ActionBtn } from "../../components/Button/TableActionButton";
import Permission, { checkPermission } from "../../components/Permission/Permission";
import { FixedType } from "rc-table/lib/interface";
import Text from "../../components/Text/Text";
import { FlexCol } from "../../components/Container/Container";
import { color, icon, image } from "../../resource";
import {
  approveFlashSale,
  checkAvailableCode,
  deleteFlashSale,
  getFlashSaleList,
} from "../../datasource/FlashSaleDatasource";
import { mapBgColor, mapStatus, mapTextColor } from "../../definitions/flashSaleStatus";
import { roleAtom } from "../../store/RoleAtom";
import { useRecoilValue } from "recoil";
import { useMessageContext } from "../../context/MessageProvider";

export const IndexFlashSale: React.FC = () => {
  const profile = JSON.parse(localStorage.getItem("profile")!);
  const { messageApi } = useMessageContext();
  const roleData = useRecoilValue(roleAtom);
  const navigate = useNavigate();
  const pageSize = 10;
  const [page, setPage] = useState<string>("1");
  const [searchDate, setSearchDate] = useState<any>([null, null]);
  const [data, setData] = useState<any>();
  const [modalConfirm, setModalConfirm] = useState<any>({
    open: false,
    action: "",
    status: false,
    name: "",
    id: null,
  });
  const [status, setStatus] = useState<string>("ALL");
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const isApprove = checkPermission(["flashSale", "approve"], roleData);
  const [modalDupCode, setModalDupCode] = useState<any>({ open: false, code: "", id: null });

  const fetchData = async () => {
    await getFlashSaleList({
      company: profile.company,
      status,
      page,
      take: pageSize,
      search: searchText,
      startDate: searchDate?.[0] ?? null,
      endDate: searchDate?.[1] ?? null,
    }).then((res) => {
      setData(res);
    });
  };

  useEffect(() => {
    fetchData();
  }, [status, page, searchText, searchDate]);

  const PageTitle = () => {
    return (
      <Row justify={"space-between"} align={"middle"} gutter={8}>
        <Col>
          <div>
            <span
              className='card-label font-weight-bolder text-dark'
              style={{ fontSize: 22, fontWeight: "bold" }}
            >
              ตั้งค่า Flash Sale
            </span>
          </div>
        </Col>
        <Col>
          <Row justify={"end"} gutter={8}>
            <Col>
              <Button
                type='primary'
                title='+ สร้าง Flash Sale'
                height={40}
                onClick={() => navigate("/PromotionPage/flashSale/0/create")}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  const columes = [
    {
      title: "ชื่อ Flash Sale",
      dataIndex: "flashSaleName",
      key: "flashSaleName",
      width: "15%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value}</Text>
              <Text level={6} color='Text3'>
                {row?.promotionCode}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "Product Brand",
      dataIndex: "productBrand",
      key: "productBrand",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "ประเภทโปรโมชัน",
      dataIndex: "promotionType",
      key: "promotionType",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value === "DISCOUNT" ? "ส่วนลด" : "-"}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "โควตา",
      dataIndex: "isQuota",
      key: "isQuota",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>
                {value ? (
                  <CheckCircleFilled style={{ color: color.success }} />
                ) : (
                  <CheckCircleOutlined style={{ color: color.Grey }} />
                )}
                &nbsp;
                {value ? "มีโควตา" : "ไม่มีโควตา"}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "ระยะเวลา",
      dataIndex: "startDate",
      key: "startDate",
      width: "15%",
      render: (value: any, row: any, index: number) => {
        const dateTime = `${dayjs(row?.startDate).format("DD/MM/YYYY")} (${dayjs(row?.startDate).format("HH:mm")}) - ${dayjs(row?.endDate).format("DD/MM/YYYY")} (${dayjs(row?.endDate).format("HH:mm")})`;
        return {
          children: (
            <FlexCol>
              {row?.startDate || row?.endDate ? (
                <>
                  <Text>{dateTime}</Text>
                </>
              ) : (
                <Text>-</Text>
              )}
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "อัปเดตโดย",
      dataIndex: "updateBy",
      key: "updateBy",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value || "-"}</Text>
              <Text level={6} color='Text3'>
                {dayjs(row?.updateAt).format("DD/MM/YYYY")}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Tag style={{ backgroundColor: mapBgColor[value], border: "none" }}>
                <Text color={mapTextColor[value]} level={6} fontWeight={600}>
                  {mapStatus[value]}
                </Text>
              </Tag>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "เปิด/ปิดการใช้งาน",
      dataIndex: "isEnabled",
      key: "isEnabled",
      hidden: !isApprove,
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol style={{ alignItems: "center" }}>
              <Switch
                disabled={
                  row.status === "DRAFT" || row.status === "EXPIRED" || row.status === "ACTIVE"
                }
                checked={value}
                onChange={async (checked) => {
                  if (checked) {
                    const result = await checkAvailableCode({
                      promotionCode: row.promotionCode,
                      flashSaleId: row.flashSaleId,
                    });
                    if (!result.success) {
                      setModalConfirm({
                        open: true,
                        action: "status",
                        status: checked,
                        name: row.flashSaleName,
                        id: row.flashSaleId,
                      });
                    } else {
                      setModalDupCode({ open: true, code: row.promotionCode, id: row.flashSaleId });
                    }
                  } else {
                    setModalConfirm({
                      open: true,
                      action: "status",
                      status: checked,
                      name: row.flashSaleName,
                      id: row.flashSaleId,
                    });
                  }
                }}
              />
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "จัดการ",
      dataIndex: "coStatus",
      key: "coStatus",
      fixed: "right" as FixedType | undefined,
      render: (value: any, row: any, index: number) => {
        const id = row.flashSaleId;
        const mapDisabled = {
          ACTIVE: true,
          EXPIRED: true,
        };
        return {
          children: (
            <Row justify={"start"} gutter={8}>
              <Col>
                <Permission permission={["flashSale", "view"]}>
                  <ActionBtn
                    onClick={() => navigate("/PromotionPage/detailFlashSale/" + id + "/detail")}
                    icon={<UnorderedListOutlined />}
                  />
                </Permission>
              </Col>
              <Col>
                <Permission permission={["flashSale", "delete"]}>
                  <ActionBtn
                    icon={
                      <DeleteOutlined
                        style={{
                          color: mapDisabled[row.status] ? color.Disable : color.error,
                          cursor: mapDisabled[row.status] ? "not-allowed" : "pointer",
                        }}
                      />
                    }
                    onClick={() =>
                      !mapDisabled[row.status] &&
                      setModalConfirm({
                        open: true,
                        action: "delete",
                        name: row.flashSaleName,
                        id: row.flashSaleId,
                      })
                    }
                  />
                </Permission>
              </Col>
            </Row>
          ),
        };
      },
    },
  ].filter((item) => !item.hidden);

  const onDelete = async () => {
    await deleteFlashSale({
      flashSaleId: modalConfirm.id,
      updateBy: profile.firstname + " " + profile.lastname,
    }).then((res) => {
      if (res.success) {
        messageApi.open({
          type: "error",
          content: "ลบสำเร็จ",
          icon: <img src={icon.delete} style={{ width: 14, height: 16 }} />,
        });
        fetchData();
      }
    });
  };

  return (
    <>
      <CardContainer>
        <PageTitle />
        <br />
        <Row justify={"space-between"} align={"middle"} gutter={8}>
          <Col span={18}>
            <Input
              allowClear
              placeholder='ค้นหารหัส / ชื่อโปรโมชัน'
              suffix={<SearchOutlined />}
              onPressEnter={(e: any) => {
                setSearchText(e.target.value);
                setPage("1");
              }}
              onChange={(e: any) => {
                !e.target.value && (setSearchText(undefined), setPage("1"));
              }}
            />
          </Col>
          <Col span={6}>
            <DatePicker.RangePicker
              format='DD/MM/YYYY'
              allowClear
              allowEmpty={[true, true]}
              // disabledDate={(current: any) => {
              //   return current && current.isAfter(dayjs().endOf("day"));
              // }}
              style={{ height: "40px", width: "100%" }}
              value={[
                searchDate[0] ? dayjs(searchDate[0]) : null,
                searchDate[1] ? dayjs(searchDate[1]) : null,
              ]}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setSearchDate([
                    dayjs(dates[0])?.format("YYYY-MM-DD"),
                    dayjs(dates[1])?.format("YYYY-MM-DD"),
                  ]);
                } else {
                  setSearchDate([undefined, undefined]);
                }
                setPage("1");
              }}
            />
          </Col>
        </Row>
        <br />
        <Tabs
          style={{ fontFamily: "IBM Plex Sans Thai" }}
          activeKey={status}
          onChange={(key) => {
            setStatus(key);
            setPage("1");
            // setParams({ status: key, page: "1" });
          }}
          items={[
            {
              label: `ทั้งหมด (${data?.countStatus?.["ALL"] || 0})`,
              key: "ALL",
            },
            {
              label: `แบบร่าง (${data?.countStatus?.["DRAFT"] || 0})`,
              key: "DRAFT",
            },
            {
              label: `รออนุมัติ (${data?.countStatus?.["WAIT_APPROVE"] || 0})`,
              key: "WAIT_APPROVE",
            },
            {
              label: `รอเปิดใช้งาน (${data?.countStatus?.["WAIT_ACTIVE"] || 0})`,
              key: "WAIT_ACTIVE",
            },
            {
              label: `เปิดใช้งาน (${data?.countStatus?.["ACTIVE"] || 0})`,
              key: "ACTIVE",
            },
            {
              label: `ปิดใช้งาน (${data?.countStatus?.["INACTIVE"] || 0})`,
              key: "INACTIVE",
            },
            {
              label: `หมดอายุ (${data?.countStatus?.["EXPIRED"] || 0})`,
              key: "EXPIRED",
            },
          ]}
        />
        <ConfigProvider
          theme={{
            components: {
              Table: {
                headerBg: "#F3F6F9",
                fontFamily: "IBM Plex Sans Thai",
                fontSize: 15,
              },
            },
          }}
        >
          <Table
            columns={columes}
            dataSource={data?.data || []}
            tableLayout='fixed'
            scroll={{
              x: "max-content",
            }}
            pagination={{
              pageSize,
              current: Number(page),
              total: data?.count,
              showSizeChanger: false,
              position: ["bottomCenter"],
              onChange: (page) => {
                const nextPage = String(page);
                setPage(nextPage);
                //setParams({ page: nextPage });
              },
            }}
            locale={{
              emptyText: (
                <div
                  style={{
                    padding: 32,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 24,
                    minHeight: 350,
                  }}
                >
                  <img
                    src={icon.emptyHistoryPoint}
                    style={{
                      width: 80,
                      height: 90,
                    }}
                    alt='empty_table'
                  />
                  <Text fontFamily='Sarabun' fontSize={14} fontWeight={400} color='Text3'>
                    ยังไม่มีรายการ
                  </Text>
                </div>
              ),
            }}
          />
        </ConfigProvider>
      </CardContainer>
      {modalConfirm.open && (
        <Modal
          open={modalConfirm.open}
          onOk={async () => {
            if (modalConfirm.action === "delete") {
              onDelete();
            } else {
              await approveFlashSale({
                flashSaleId: modalConfirm.id,
                isEnabled: modalConfirm.status,
                updateBy: profile.firstname + " " + profile.lastname,
              }).then((res) => {
                if (res.success) {
                  messageApi.success({
                    type: "success",
                    content: "บันทึกสำเร็จ",
                  });
                  fetchData();
                }
              });
            }
            setModalConfirm({ ...modalConfirm, open: false });
          }}
          onCancel={() => setModalConfirm({ ...modalConfirm, open: false })}
          centered
          cancelText='ยกเลิก'
          okText='ยืนยัน'
          okButtonProps={{
            style: {
              backgroundColor: modalConfirm.action === "status" ? color.primary : color.error,
              borderColor: modalConfirm.action === "status" ? color.primary : color.error,
            },
          }}
          cancelButtonProps={{
            style: {
              borderColor: modalConfirm.action === "status" ? color.primary : color.error,
              color: modalConfirm.action === "status" ? color.primary : color.error,
            },
          }}
        >
          <Text level={2} fontWeight={600}>
            ยืนยันการ
            {modalConfirm.action === "status"
              ? modalConfirm.status
                ? "เปิดใช้งาน"
                : "ปิดใช้งาน"
              : modalConfirm.action === "delete"
                ? "ลบ"
                : ""}
          </Text>
          <br />
          <Text level={2} fontWeight={600}>
            {`"${modalConfirm.name}"`}
          </Text>
          <br />
          <Text color='Text3'>โปรดตรวจสอบรายละเอียดอีกครั้งก่อนกดยืนยัน</Text>
          <Divider size='small' />
        </Modal>
      )}
      {modalDupCode.open && (
        <Modal
          open={modalDupCode.open}
          onOk={() => setModalDupCode({ ...modalDupCode, open: false })}
          onCancel={() => setModalDupCode({ ...modalDupCode, open: false })}
          centered
          footer={null}
        >
          <Row justify='center'>
            <img src={image.errorState} style={{ width: 250, height: 150 }} />
          </Row>
          <div style={{ textAlign: "center" }}>
            <Text level={3} fontWeight={600}>
              ไม่สามารถเปิดใช้งาน Flash Sale ได้
            </Text>
          </div>
          <br />
          <FlexCol style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Text color='Text3'>รหัสโปรโมชัน {modalDupCode.code} ซ้ำกับแคมเปญอื่นในระบบ</Text>
            <Text color='Text3'>กรุณาแก้ไขรหัสโปรโมชันไม่ให้ซ้ำ แล้วลองเปิดใช้งานอีกครั้ง</Text>
          </FlexCol>
          <Divider size='small' />
          <Row justify='space-between' gutter={16}>
            <Col span={12}>
              <Button
                typeButton='primary-light'
                onClick={() => setModalDupCode({ ...modalDupCode, open: false })}
                title='กลับหน้ารายการ'
              />
            </Col>
            <Col span={12}>
              <Button
                typeButton='primary'
                onClick={() => navigate("/PromotionPage/flashSale/" + modalDupCode.id + "/edit")}
                title='แก้ไขรหัสโปรโมชัน'
              />
            </Col>
          </Row>
        </Modal>
      )}
    </>
  );
};
